import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import styles from '../components/enquiries/Enquiries.module.css';
import JewellerCard from '../components/community/JewellerCard';
import JewellerProfileModal from '../components/community/JewellerProfileModal';
import { communityGroups, communityPortraits } from '../data/community';
import {
  fetchActiveCommunityJewellers,
  shuffleJewellers
} from '../services/communityJewellers';

const featuredCount = 4;

const CommunityHub = () => {
  const groups = Array.isArray(communityGroups) ? communityGroups : [];
  const portraits = Array.isArray(communityPortraits) ? communityPortraits : [];
  const portraitColumns = Array.from({ length: 4 }, (_, columnIndex) =>
    portraits.filter((_, index) => index % 4 === columnIndex)
  );
  const [allJewellers, setAllJewellers] = useState([]);
  const [jewellersLoading, setJewellersLoading] = useState(true);
  const [selectedJeweller, setSelectedJeweller] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadJewellers = async () => {
      try {
        const results = await fetchActiveCommunityJewellers();
        if (mounted) {
          setAllJewellers(results);
        }
      } catch (error) {
        console.error('Error loading active jewellers:', error);
        if (mounted) {
          setAllJewellers([]);
        }
      } finally {
        if (mounted) {
          setJewellersLoading(false);
        }
      }
    };

    loadJewellers();

    return () => {
      mounted = false;
    };
  }, []);

  const featuredJewellers = useMemo(
    () => shuffleJewellers(allJewellers).slice(0, featuredCount),
    [allJewellers]
  );

  return (
    <div className={styles.container}>
      <div
        className={styles.header}
        style={{
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <style>
          {`
            @keyframes communityScrollUp {
              from { transform: translateY(0); }
              to { transform: translateY(-50%); }
            }

            @keyframes communityScrollDown {
              from { transform: translateY(-50%); }
              to { transform: translateY(0); }
            }
          `}
        </style>
        <div
          style={{
            position: 'relative',
            minHeight: '160px'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              paddingRight: 'clamp(280px, 32vw, 360px)',
              minHeight: '160px'
            }}
          >
            <div className={styles.headerText}>
              <h1 className={styles.headerTitle}>Community</h1>
              <p className={styles.headerSubtitle}>
                A single place for you to promote your brand, shape the portal,
                grow the network, and contribute to the wider Boutee story. Boutee is a community-driven platform, and we want to make it as easy as possible for you to get involved and make an impact.
              </p>
            </div>
          </div>

          <div
            style={{
              position: 'absolute',
              top: '1px',
              right: 0,
              bottom: '1px',
              width: 'clamp(260px, 30vw, 340px)',
              borderRadius: 'var(--radius-xl)',
              overflow: 'hidden',
              background: 'linear-gradient(180deg, var(--Grey-50) 0%, var(--Surface-Secondary) 100%)',
              borderLeft: '1px solid var(--Stroke-Light)',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '6px'
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, var(--Surface-Primary) 0%, transparent 10%, transparent 90%, var(--Surface-Primary) 100%)',
                pointerEvents: 'none',
                zIndex: 1
              }}
            />

            {portraitColumns.map((column, columnIndex) => (
              <div
                key={`column-${columnIndex}`}
                style={{
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    animation: `${columnIndex % 2 === 0 ? 'communityScrollUp' : 'communityScrollDown'} ${42 + columnIndex * 4}s linear infinite`
                  }}
                >
                  {[...column, ...column].map((imagePath, index) => (
                    <div
                      key={`${imagePath}-${index}`}
                      style={{
                        borderRadius: '14px',
                        overflow: 'hidden',
                        aspectRatio: '1 / 1',
                        background: 'var(--Grey-100)',
                        boxShadow: 'var(--shadow-sm)'
                      }}
                    >
                      <img
                        src={imagePath}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'var(--spacing-l)'
        }}
      >
        {groups.map((group) => {
          const Icon = group.icon;

          return (
            <Link
              key={group.slug}
              to={`/community/${group.slug}`}
              style={{
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <article
                style={{
                  background: 'var(--Surface-Primary)',
                  border: 'var(--card-border)',
                  borderRadius: 'var(--radius-xl)',
                  padding: 'var(--spacing-xl)',
                  boxShadow: 'var(--shadow-card)',
                  minHeight: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-frame-big)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-card)';
                }}
              >
                <div
                  style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: 'var(--radius-m)',
                    background: group.accent.background,
                    border: `1px solid ${group.accent.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 'var(--spacing-l)'
                  }}
                >
                  <Icon
                    style={{
                      width: '24px',
                      height: '24px',
                      color: group.accent.foreground
                    }}
                  />
                </div>

                <h2
                  style={{
                    margin: '0 0 var(--spacing-xs) 0',
                    color: 'var(--Text-Primary)',
                    fontSize: 'var(--font-size-subtitle1)',
                    fontWeight: 'var(--font-weight-subtitle1)'
                  }}
                >
                  {group.title}
                </h2>

                <p
                  style={{
                    margin: '0 0 var(--spacing-m) 0',
                    color: 'var(--Text-Secondary)',
                    fontSize: 'var(--font-size-body3)',
                    lineHeight: '1.6'
                  }}
                >
                  {group.shortDescription}
                </p>

                <ul
                  style={{
                    margin: '0 0 var(--spacing-l) 0',
                    paddingLeft: '18px',
                    listStyleType: 'disc',
                    color: 'var(--Text-Secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    flex: 1
                  }}
                >
                  {group.items.slice(0, 3).map((item) => (
                    <li
                      key={item.title}
                      style={{
                        fontSize: 'var(--font-size-body4)',
                        lineHeight: '1.5'
                      }}
                    >
                      {item.title}
                    </li>
                  ))}
                </ul>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-xs)',
                    marginTop: 'auto',
                    color: group.accent.foreground,
                    fontSize: 'var(--font-size-body4)',
                    fontWeight: 'var(--font-weight-medium)'
                  }}
                >
                  <span>See opportunities</span>
                  <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
                </div>
              </article>
            </Link>
          );
        })}
      </section>

      <section style={{ marginTop: 'var(--spacing-xxl)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--spacing-m)',
            marginBottom: 'var(--spacing-m)'
          }}
        >
          <div>
            <h2
              style={{
                margin: '0 0 var(--spacing-xxs) 0',
                color: 'var(--Text-Primary)',
                fontSize: 'var(--font-size-subtitle1)',
                fontWeight: 'var(--font-weight-subtitle1)'
              }}
            >
              Meet the jewellers on Boutee
            </h2>
            <p
              style={{
                margin: 0,
                color: 'var(--Text-Secondary)',
                fontSize: 'var(--font-size-body3)',
                lineHeight: '1.6'
              }}
            >
              Discover jewellers currently live on Boutee and learn a little more about the wider community.
            </p>
          </div>

          {!jewellersLoading && featuredJewellers.length > 0 && (
            <Link
              to="/community/all-jewellers"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
                padding: '12px 18px',
                borderRadius: '999px',
                border: '1px solid var(--Stroke-Light)',
                background: 'var(--Surface-Primary)',
                color: 'var(--Text-Primary)',
                textDecoration: 'none',
                fontSize: 'var(--font-size-body4)',
                fontWeight: 'var(--font-weight-medium)',
                boxShadow: 'var(--shadow-sm)',
                whiteSpace: 'nowrap'
              }}
            >
              <span>See all jewellers</span>
              <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
            </Link>
          )}
        </div>

        {jewellersLoading ? (
          <div
            style={{
              background: 'var(--Surface-Primary)',
              border: 'var(--card-border)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--spacing-xl)',
              boxShadow: 'var(--shadow-card)',
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          >
            Loading live jewellers...
          </div>
        ) : featuredJewellers.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 'var(--spacing-l)',
              alignItems: 'stretch'
            }}
          >
            {featuredJewellers.map((jeweller) => (
              <div key={jeweller.id}>
                <JewellerCard jeweller={jeweller} onClick={setSelectedJeweller} />
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              background: 'var(--Surface-Primary)',
              border: 'var(--card-border)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--spacing-xl)',
              boxShadow: 'var(--shadow-card)',
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body3)'
            }}
          >
            No live jewellers to show just yet.
          </div>
        )}
      </section>

      <Link
        to="/contact-support"
        style={{
          display: 'block',
          marginTop: 'var(--spacing-l)',
          textDecoration: 'none',
          color: 'inherit'
        }}
      >
        <article
          style={{
            background: 'var(--Surface-Primary)',
            border: 'var(--card-border)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-xl)',
            boxShadow: 'var(--shadow-card)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = 'var(--shadow-frame-big)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'var(--shadow-card)';
          }}
        >
          <h2
            style={{
              margin: '0 0 var(--spacing-xs) 0',
              color: 'var(--Text-Primary)',
              fontSize: 'var(--font-size-subtitle1)',
              fontWeight: 'var(--font-weight-subtitle1)'
            }}
          >
            Want to contribute in another way?
          </h2>
          <p
            style={{
              margin: '0 0 var(--spacing-m) 0',
              color: 'var(--Text-Secondary)',
              fontSize: 'var(--font-size-body3)',
              lineHeight: '1.6'
            }}
          >
            If what you’re looking for is not listed here, tell us what you have in mind and we’ll point you in the right direction.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-xs)',
              color: 'var(--Text-Primary)',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            <span>Get in touch</span>
            <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
          </div>
        </article>
      </Link>

      {selectedJeweller && (
        <JewellerProfileModal
          jeweller={selectedJeweller}
          onClose={() => setSelectedJeweller(null)}
        />
      )}
    </div>
  );
};

export default CommunityHub;
